import { RowCrudStatus } from "../../common/types";
import {
  AuthorComposer,
  AuthorComposerCreateMutationVariables,
  AuthorComposerUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { Gender } from "../../../api/graphql";
import { TaxIdType } from "../../../api/graphql";
import { blankToNull } from "../../../common/blankToNull";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { CuitCertificateErrorType } from "../../../api/graphql";
import { countryDisplayIdByDemonym } from "../../country/store/CountryCommonStore";
import { diaDeCalendario } from "../../../common/calendarDate";
import { countryDisplayId } from "../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../provinceState/store/ProvinceStateCommonStore";
import {
  AuthorCollectingSocietyValuesType,
  getAuthorCollectingSocietyValues,
  getAuthorCollectingSocietyCreateInput,
  getAuthorCollectingSocietyUpdateInput,
} from "../../authorCollectingSociety/store/AuthorCollectingSocietyCommonStore";
import {
  addedStatusList,
  originalDeletedStatusList,
  originalUpdatedStatusList,
} from "../../common/types";
import {
  AuthorPublisherValuesType,
  getAuthorPublisherValues,
  getAuthorPublisherCreateInput,
  getAuthorPublisherUpdateInput,
} from "../../authorPublisher/store/AuthorPublisherCommonStore";

let uniqueCounter = 1;
export type AuthorComposerValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  firstName: string;
  lastName: string;
  gender: Gender | "";
  taxIdType: TaxIdType | "";
  taxId: string;
  cuitCertificate: string;
  cuitCertificate_data: FileCRUDType;
  __cuitCertificate_files: FileCRUDType[];
  cuitCertificateIssuedAt: Date | null;
  cuitCertificateError: string;
  cuitCertificateErrorType: CuitCertificateErrorType | "";
  dniDeliveredToSadaicAt: Date | null;
  dniValidatedAt: Date | null;
  dniValidatedFields: string;
  email: string;
  phone: string;
  __nationality: string;
  showNationality: { displayId: string; id: string } | null;
  dateOfBirth: string | null;
  streetAndNumber: string;
  floor: string;
  suite: string;
  postalCode: string;
  __country: string;
  showCountry: { displayId: string; id: string } | null;
  __province: string;
  showProvince: { displayId: string; id: string } | null;
  city: string;
  aka: string;
  ipi: string;
  showManagementSocieties: AuthorCollectingSocietyValuesType[];
  showPublishers: AuthorPublisherValuesType[];
  createdAt_: Date | null;
  lastUpdatedAt_: Date | null;
};

export type AuthorComposerErrorsType = {
  firstName?: { message: string; fieldLabel: string };
  lastName?: { message: string; fieldLabel: string };
  gender?: { message: string; fieldLabel: string };
  taxIdType?: { message: string; fieldLabel: string };
  taxId?: { message: string; fieldLabel: string };
  cuitCertificate?: { message: string; fieldLabel: string };
  cuitCertificateIssuedAt?: { message: string; fieldLabel: string };
  cuitCertificateError?: { message: string; fieldLabel: string };
  cuitCertificateErrorType?: { message: string; fieldLabel: string };
  dniDeliveredToSadaicAt?: { message: string; fieldLabel: string };
  dniValidatedAt?: { message: string; fieldLabel: string };
  dniValidatedFields?: { message: string; fieldLabel: string };
  email?: { message: string; fieldLabel: string };
  phone?: { message: string; fieldLabel: string };
  nationality?: { message: string; fieldLabel: string };
  dateOfBirth?: { message: string; fieldLabel: string };
  streetAndNumber?: { message: string; fieldLabel: string };
  floor?: { message: string; fieldLabel: string };
  suite?: { message: string; fieldLabel: string };
  postalCode?: { message: string; fieldLabel: string };
  country?: { message: string; fieldLabel: string };
  province?: { message: string; fieldLabel: string };
  city?: { message: string; fieldLabel: string };
  aka?: { message: string; fieldLabel: string };
  ipi?: { message: string; fieldLabel: string };
  managementSocieties?: { message: string; fieldLabel: string };
  publishers?: { message: string; fieldLabel: string };
};

export function AuthorComposerValuesInit(): AuthorComposerValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    firstName: "",
    lastName: "",
    gender: "" as Gender | "",
    taxIdType: "cuitCuil" as TaxIdType,
    taxId: "",
    cuitCertificate: "",
    cuitCertificate_data: fileCRUDInit(),
    __cuitCertificate_files: [],
    cuitCertificateIssuedAt: null,
    cuitCertificateError: "",
    cuitCertificateErrorType: "" as CuitCertificateErrorType | "",
    dniDeliveredToSadaicAt: null,
    dniValidatedAt: null,
    dniValidatedFields: "{}",
    email: "",
    phone: "",
    __nationality: "",
    showNationality: null,
    dateOfBirth: null,
    streetAndNumber: "",
    floor: "",
    suite: "",
    postalCode: "",
    __country: "",
    showCountry: null,
    __province: "",
    showProvince: null,
    city: "",
    aka: "",
    ipi: "",
    showManagementSocieties: [],
    showPublishers: [],
    createdAt_: null,
    lastUpdatedAt_: null,
  };
}

export type AuthorComposerType = AuthorComposer | AuthorComposerValuesType;

export function authorComposerDisplayId(
  source: Partial<AuthorComposer>,
  _langStore: LangStore
) {
  return (
    (source.firstName ?? _langStore.emptyStringSymbol) +
    " " +
    (source.lastName ?? _langStore.emptyStringSymbol)
  );
}

export function getAuthorComposerValues(
  source: AuthorComposerType,
  _langStore: LangStore,
  _duplicated: boolean = false
): AuthorComposerValuesType {
  const entityValues = AuthorComposerValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : authorComposerDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.firstName = source.firstName;
  entityValues.lastName = source.lastName;
  entityValues.gender = source.gender || ("" as Gender | "");
  entityValues.taxIdType = source.taxIdType || ("" as TaxIdType | "");
  entityValues.taxId = source.taxId || "";
  entityValues.__cuitCertificate_files =
    source.cuitCertificate_data && !_duplicated
      ? [
          {
            id:
              "id" in source.cuitCertificate_data &&
              source.cuitCertificate_data.id
                ? source.cuitCertificate_data.id
                : `generated_${uniqueCounter++}`,
            name: source.cuitCertificate_data.name,
            description: source.cuitCertificate_data.description || undefined,
            size: source.cuitCertificate_data.size || 0,
            formattedSize: source.cuitCertificate_data.size
              ? formatBytes(source.cuitCertificate_data.size)
              : "",
            mimetype: source.cuitCertificate_data.mimetype,
            url: source.cuitCertificate_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.cuitCertificate =
    source.cuitCertificate && !_duplicated ? source.cuitCertificate : "";
  entityValues.cuitCertificate_data =
    source.cuitCertificate_data && "__status__" in source.cuitCertificate_data
      ? source.cuitCertificate_data
      : source.cuitCertificate_data
      ? {
          id: ("id" in source.cuitCertificate_data &&
          source.cuitCertificate_data.id
            ? source.cuitCertificate_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.cuitCertificate_data.name,
          description: source.cuitCertificate_data.description || undefined,
          size: source.cuitCertificate_data.size || 0,
          formattedSize: source.cuitCertificate_data.size
            ? formatBytes(source.cuitCertificate_data.size)
            : "",
          mimetype: source.cuitCertificate_data.mimetype,
          url: source.cuitCertificate_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.cuitCertificateIssuedAt = source.cuitCertificateIssuedAt
    ? new Date(source.cuitCertificateIssuedAt)
    : null;
  entityValues.cuitCertificateError = source.cuitCertificateError || "";
  entityValues.cuitCertificateErrorType =
    source.cuitCertificateErrorType || ("" as CuitCertificateErrorType | "");
  entityValues.dniDeliveredToSadaicAt = source.dniDeliveredToSadaicAt
    ? new Date(source.dniDeliveredToSadaicAt)
    : null;
  entityValues.dniValidatedAt = source.dniValidatedAt
    ? new Date(source.dniValidatedAt)
    : null;
  entityValues.dniValidatedFields = source.dniValidatedFields || "";
  entityValues.email = source.email;
  entityValues.phone = source.phone || "";
  entityValues.showNationality =
    "nationality" in source && source.nationality
      ? {
          id: source.nationality.id,
          displayId: countryDisplayIdByDemonym(source.nationality, _langStore),
        }
      : "showNationality" in source && source.showNationality
      ? {
          id: source.showNationality.id,
          displayId: source.showNationality.displayId,
        }
      : null;
  entityValues.__nationality = entityValues.showNationality?.displayId || "";
  entityValues.dateOfBirth = diaDeCalendario(source.dateOfBirth);
  entityValues.streetAndNumber = source.streetAndNumber || "";
  entityValues.floor = source.floor || "";
  entityValues.suite = source.suite || "";
  entityValues.postalCode = source.postalCode || "";
  entityValues.showCountry =
    "country" in source && source.country
      ? {
          id: source.country.id,
          displayId: countryDisplayId(source.country, _langStore),
        }
      : "showCountry" in source && source.showCountry
      ? { id: source.showCountry.id, displayId: source.showCountry.displayId }
      : null;
  entityValues.__country = entityValues.showCountry?.displayId || "";
  entityValues.showProvince =
    "province" in source && source.province
      ? {
          id: source.province.id,
          displayId: provinceStateDisplayId(source.province, _langStore),
        }
      : "showProvince" in source && source.showProvince
      ? { id: source.showProvince.id, displayId: source.showProvince.displayId }
      : null;
  entityValues.__province = entityValues.showProvince?.displayId || "";
  entityValues.city = source.city || "";
  entityValues.aka = source.aka || "";
  entityValues.ipi = source.ipi || "";
  entityValues.showManagementSocieties =
    "managementSocieties" in source && source.managementSocieties
      ? (source.managementSocieties || []).map((x) => {
          const row: AuthorCollectingSocietyValuesType =
            getAuthorCollectingSocietyValues(x, _langStore, _duplicated);
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showManagementSocieties" in source && source.showManagementSocieties
      ? (source.showManagementSocieties || []).map((x) => {
          const row: AuthorCollectingSocietyValuesType =
            getAuthorCollectingSocietyValues(x, _langStore, _duplicated);
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.showPublishers =
    "publishers" in source && source.publishers
      ? (source.publishers || []).map((x) => {
          const row: AuthorPublisherValuesType = getAuthorPublisherValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showPublishers" in source && source.showPublishers
      ? (source.showPublishers || []).map((x) => {
          const row: AuthorPublisherValuesType = getAuthorPublisherValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.createdAt_ = source.createdAt_
    ? new Date(source.createdAt_)
    : null;
  entityValues.lastUpdatedAt_ = source.lastUpdatedAt_
    ? new Date(source.lastUpdatedAt_)
    : null;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getAuthorComposerUpdateVariables(
  values: AuthorComposerValuesType
): AuthorComposerUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    firstName: values.firstName,
    lastName: values.lastName,
    gender: values.gender || null,
    taxIdType: values.taxIdType || null,
    taxId: blankToNull(values.taxId),
    email: values.email,
    phone: blankToNull(values.phone),
    nationalityId: values.showNationality?.id || "",
    dateOfBirth: values.dateOfBirth || null,
    streetAndNumber: blankToNull(values.streetAndNumber),
    floor: blankToNull(values.floor),
    suite: blankToNull(values.suite),
    postalCode: blankToNull(values.postalCode),
    countryId: values.showCountry?.id || "",
    provinceId: values.showProvince?.id || "",
    city: blankToNull(values.city),
    aka: blankToNull(values.aka),
    ipi: blankToNull(values.ipi),
    managementSocieties: {
      create: values.showManagementSocieties
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getAuthorCollectingSocietyCreateInput(x)),
      update: values.showManagementSocieties
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getAuthorCollectingSocietyUpdateInput(x)),
      remove: values.showManagementSocieties
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
    publishers: {
      create: values.showPublishers
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getAuthorPublisherCreateInput(x)),
      update: values.showPublishers
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getAuthorPublisherUpdateInput(x)),
      remove: values.showPublishers
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
  };
}

export function getAuthorComposerCreateVariables(
  values: AuthorComposerValuesType
): AuthorComposerCreateMutationVariables {
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    gender: values.gender || null,
    taxIdType: values.taxIdType || null,
    taxId: blankToNull(values.taxId),
    email: values.email,
    phone: blankToNull(values.phone),
    nationalityId: values.showNationality?.id || "",
    dateOfBirth: values.dateOfBirth || null,
    streetAndNumber: blankToNull(values.streetAndNumber),
    floor: blankToNull(values.floor),
    suite: blankToNull(values.suite),
    postalCode: blankToNull(values.postalCode),
    countryId: values.showCountry?.id || "",
    provinceId: values.showProvince?.id || "",
    city: blankToNull(values.city),
    aka: blankToNull(values.aka),
    ipi: blankToNull(values.ipi),
    managementSocieties: values.showManagementSocieties
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getAuthorCollectingSocietyCreateInput(x)),
    publishers: values.showPublishers
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getAuthorPublisherCreateInput(x)),
  };
}
